import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Gift, Copy, Check, Share2, Users, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralSection() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/referral/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(data);
    } catch (e) {
      console.error('Failed to fetch referral stats:', e);
    } finally {
      setLoading(false);
    }
  };

  const referralLink = stats?.referral_code
    ? `${window.location.origin}/register?ref=${stats.referral_code}`
    : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(stats?.referral_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareLink = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join LocalFlame!',
          text: `Join me on LocalFlame — the dating app with NO subscriptions. Use my code: ${stats?.referral_code}`,
          url: referralLink
        });
      } catch (e) {
        copyLink();
      }
    } else {
      copyLink();
    }
  };

  if (loading) {
    return (
      <div className="p-6 rounded-2xl bg-zinc-900/50 border border-zinc-800">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 text-rose-500 animate-spin" />
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const progressPercent = Math.min(100, (stats.paid_referrals / stats.referrals_needed) * 100);

  return (
    <div className="p-6 rounded-2xl bg-gradient-to-br from-rose-600/10 via-zinc-900/50 to-zinc-900/50 border border-rose-500/20">
      <div className="flex items-center gap-2 mb-4">
        <Gift className="w-5 h-5 text-rose-500" />
        <h3 className="font-outfit font-semibold text-white">Refer & Get Free Access</h3>
      </div>

      {/* Progress */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-zinc-400 text-sm">
            {stats.paid_referrals} of {stats.referrals_needed} friends paid
          </span>
          {stats.unlocked ? (
            <span className="flex items-center gap-1 text-green-500 text-sm font-medium">
              <Check className="w-4 h-4" /> Unlocked!
            </span>
          ) : (
            <span className="text-zinc-500 text-sm">
              {stats.remaining} more to go
            </span>
          )}
        </div>
        <Progress value={progressPercent} className="h-2 bg-zinc-800" />
      </div>

      {/* Referral Code */}
      <div className="mb-4 p-3 rounded-xl bg-zinc-950/50 border border-zinc-800">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-zinc-500 text-xs mb-1">Your Referral Code</p>
            <p data-testid="referral-code-display" className="font-mono text-xl font-bold text-white tracking-widest">
              {stats.referral_code}
            </p>
          </div>
          <button
            data-testid="copy-referral-code"
            onClick={copyCode}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm transition-colors"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      </div>

      {/* Share Link */}
      <div className="mb-4 p-3 rounded-xl bg-zinc-950/50 border border-zinc-800">
        <p className="text-zinc-500 text-xs mb-1">Share Link</p>
        <p className="text-zinc-300 text-sm truncate mb-2">{referralLink}</p>
        <div className="flex gap-2">
          <Button
            data-testid="copy-referral-link"
            onClick={copyLink}
            variant="outline"
            size="sm"
            className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <Copy className="w-4 h-4 mr-1" />
            Copy Link
          </Button>
          <Button
            data-testid="share-referral-link"
            onClick={shareLink}
            size="sm"
            className="flex-1 bg-rose-600 hover:bg-rose-500 text-white"
          >
            <Share2 className="w-4 h-4 mr-1" />
            Share
          </Button>
        </div>
      </div>

      {/* Referred Users */}
      {stats.referred_users && stats.referred_users.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Users className="w-4 h-4 text-zinc-400" />
            <span className="text-zinc-400 text-sm">Your Referrals ({stats.total_referrals})</span>
          </div>
          <div className="space-y-2">
            {stats.referred_users.map((ru) => (
              <div key={ru._id} className="flex items-center justify-between py-1.5">
                <span className="text-zinc-300 text-sm">{ru.name}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  ru.is_paid 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-zinc-800 text-zinc-500'
                }`}>
                  {ru.is_paid ? 'Paid' : 'Signed Up'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Explainer */}
      <p className="text-zinc-600 text-xs mt-4">
        Invite 3 friends who pay the $19.99 fee, and you get free access to all features!
      </p>
    </div>
  );
}
